<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Journeys</h5>
        <p class="page-sub">Automate customer engagement with multi-step journeys</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click.stop="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="doExport('csv')">CSV</button>
            <button class="export-opt" @click="doExport('pdf')">PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_journeys" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Journey
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search journeys..." />
      <select v-model="filterStatus" @change="page=1; fetchJourneys()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="active">Active</option>
        <option value="paused">Paused</option>
        <option value="completed">Completed</option>
      </select>
      <input v-model="dateFrom" type="date" class="filter-input" style="max-width:160px" />
      <input v-model="dateTo" type="date" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading journeys...</p></div>
      <div v-else-if="!journeys.length" class="empty-state">
        <font-awesome-icon icon="route" class="empty-icon" />
        <p>No journeys found</p>
        <span>Create a journey to automate customer touchpoints</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Name</th><th>Trigger</th><th>Status</th><th>Enrolled</th><th>Completed</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="j in journeys" :key="j.id">
            <td class="name-col">{{ j.name }}</td>
            <td>{{ j.trigger_type ?? '—' }}</td>
            <td><span :class="['status-badge', `status--${j.status}`]">{{ j.status }}</span></td>
            <td>{{ j.enrolled_count ?? 0 }}</td>
            <td>{{ j.completed_count ?? 0 }}</td>
            <td>{{ fmtDate(j.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.edit_journeys && j.status === 'draft'" class="btn-link btn-link--activate" @click="activateJourney(j)">Activate</button>
                <button v-if="permission?.edit_journeys && j.status === 'active'" class="btn-link btn-link--pause" @click="pauseJourney(j)">Pause</button>
                <button v-if="permission?.edit_journeys" class="btn-link btn-link--edit" @click="openEdit(j)">Edit</button>
                <button v-if="permission?.delete_journeys && j.status === 'draft'" class="btn-link btn-link--del" @click="confirmDelete(j)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta && meta.last_page > 1" class="pagination">
        <button :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)" class="pg-btn">‹ Prev</button>
        <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)" class="pg-btn">Next ›</button>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box modal-box--journey">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Journey' : 'New Journey' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body modal-body--journey">
            <!-- Basic details -->
            <div class="form-group">
              <label class="form-label">Journey Name <span class="req">*</span></label>
              <input v-model="form.name" class="form-input" required />
            </div>
            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">Trigger Type</label>
                <select v-model="form.trigger_type" class="form-input">
                  <option value="contact_created">Contact Created</option>
                  <option value="lead_converted">Lead Converted</option>
                  <option value="opportunity_stage_change">Opportunity Stage Change</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Enrollment</label>
                <select v-model="form.enrollment_type" class="form-input">
                  <option value="once">Once per contact</option>
                  <option value="always">Every trigger</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Description</label>
              <textarea v-model="form.description" class="form-input" rows="2"></textarea>
            </div>

            <!-- Steps -->
            <div class="steps-section">
              <div class="steps-header">
                <span class="steps-title">Steps</span>
                <button type="button" class="btn-add-step" @click="addStep">
                  <font-awesome-icon icon="plus" class="me-1" /> Add Step
                </button>
              </div>

              <div v-if="!form.steps.length" class="steps-empty">No steps yet — add your first step above.</div>

              <div v-for="(step, i) in form.steps" :key="i" class="step-card">
                <div class="step-card-head">
                  <span class="step-num">Step {{ i + 1 }}</span>
                  <select v-model="step.type" @change="onStepTypeChange(i)" class="step-type-select">
                    <option value="wait">Wait / Delay</option>
                    <option value="email">Send Email</option>
                    <option value="sms">Send SMS</option>
                    <option value="update_field">Update Field</option>
                    <option value="create_task">Create Task</option>
                    <option value="condition">Condition</option>
                    <option value="end">End Journey</option>
                  </select>
                  <div class="step-btns">
                    <button type="button" class="step-btn" :disabled="i === 0" @click="moveStep(i, -1)" title="Move up">↑</button>
                    <button type="button" class="step-btn" :disabled="i === form.steps.length - 1" @click="moveStep(i, 1)" title="Move down">↓</button>
                    <button type="button" class="step-btn step-btn--del" @click="removeStep(i)" title="Remove">×</button>
                  </div>
                </div>

                <!-- wait -->
                <div v-if="step.type === 'wait'" class="step-config">
                  <div class="step-row">
                    <div class="form-group">
                      <label class="form-label">Days</label>
                      <input v-model.number="step.config.delay_days" type="number" min="0" class="form-input" />
                    </div>
                    <div class="form-group">
                      <label class="form-label">Hours</label>
                      <input v-model.number="step.config.delay_hours" type="number" min="0" max="23" class="form-input" />
                    </div>
                  </div>
                </div>

                <!-- email -->
                <div v-else-if="step.type === 'email'" class="step-config">
                  <div class="form-group">
                    <label class="form-label">Subject</label>
                    <input v-model="step.config.subject" class="form-input" placeholder="Email subject..." />
                  </div>
                  <div class="form-group">
                    <label class="form-label">Body</label>
                    <textarea v-model="step.config.body" class="form-input" rows="3" placeholder="Email body..."></textarea>
                  </div>
                </div>

                <!-- sms -->
                <div v-else-if="step.type === 'sms'" class="step-config">
                  <div class="form-group">
                    <label class="form-label">Message</label>
                    <textarea v-model="step.config.message" class="form-input" rows="2" placeholder="SMS message..."></textarea>
                  </div>
                </div>

                <!-- update_field -->
                <div v-else-if="step.type === 'update_field'" class="step-config">
                  <div class="step-row">
                    <div class="form-group">
                      <label class="form-label">Field</label>
                      <select v-model="step.config.field" class="form-input">
                        <option value="">Select field...</option>
                        <option value="lifecycle_stage">Lifecycle Stage</option>
                        <option value="status">Status</option>
                        <option value="phone">Phone</option>
                        <option value="industry">Industry</option>
                        <option value="source">Source</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label class="form-label">Value</label>
                      <input v-model="step.config.value" class="form-input" placeholder="New value..." />
                    </div>
                  </div>
                </div>

                <!-- create_task -->
                <div v-else-if="step.type === 'create_task'" class="step-config">
                  <div class="step-row">
                    <div class="form-group" style="flex:2">
                      <label class="form-label">Task Title</label>
                      <input v-model="step.config.title" class="form-input" placeholder="Task title..." />
                    </div>
                    <div class="form-group" style="flex:1">
                      <label class="form-label">Due in (days)</label>
                      <input v-model.number="step.config.due_in_days" type="number" min="0" class="form-input" />
                    </div>
                  </div>
                </div>

                <!-- condition -->
                <div v-else-if="step.type === 'condition'" class="step-config">
                  <div class="step-row">
                    <div class="form-group">
                      <label class="form-label">Field</label>
                      <select v-model="step.config.field" class="form-input">
                        <option value="">Select field...</option>
                        <option value="lifecycle_stage">Lifecycle Stage</option>
                        <option value="status">Status</option>
                        <option value="email">Email</option>
                        <option value="source">Source</option>
                        <option value="industry">Industry</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label class="form-label">Operator</label>
                      <select v-model="step.config.operator" class="form-input">
                        <option value="equals">Equals</option>
                        <option value="not_equals">Not Equals</option>
                        <option value="contains">Contains</option>
                        <option value="is_empty">Is Empty</option>
                        <option value="is_not_empty">Is Not Empty</option>
                      </select>
                    </div>
                    <div class="form-group" v-if="!['is_empty','is_not_empty'].includes(step.config.operator)">
                      <label class="form-label">Value</label>
                      <input v-model="step.config.value" class="form-input" placeholder="Value..." />
                    </div>
                  </div>
                </div>

                <!-- end — no config needed -->
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Journey') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header"><span class="modal-title">Delete Journey</span><button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body"><p>Delete <strong>{{ deleteTarget?.name }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- Activate Journey Confirm -->
    <div class="modal-overlay" :class="{ open: !!activateTarget }" @click.self="activateTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--green"><font-awesome-icon icon="play" /></div>
          <div>
            <span class="modal-title">Activate Journey</span>
            <p class="modal-sub">This will start enrolling contacts</p>
          </div>
          <button class="modal-close" @click="activateTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <p class="action-name">{{ activateTarget?.name }}</p>
            <p class="action-meta" v-if="activateTarget?.trigger_type">Trigger: <strong>{{ activateTarget.trigger_type }}</strong></p>
          </div>
          <p class="action-note">Once active, contacts will begin entering this journey automatically based on the trigger.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="activateTarget = null">Cancel</button>
          <button class="btn-activate" @click="doActivate" :disabled="submitting">
            <font-awesome-icon icon="play" class="me-1" />
            {{ submitting ? 'Activating…' : 'Activate Journey' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Pause Journey Confirm -->
    <div class="modal-overlay" :class="{ open: !!pauseTarget }" @click.self="pauseTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--amber"><font-awesome-icon icon="pause" /></div>
          <div>
            <span class="modal-title">Pause Journey</span>
            <p class="modal-sub">New enrolments will stop</p>
          </div>
          <button class="modal-close" @click="pauseTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <p class="action-name">{{ pauseTarget?.name }}</p>
          </div>
          <p class="action-note">Contacts already enrolled will complete their current step but no new contacts will be enrolled.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="pauseTarget = null">Cancel</button>
          <button class="btn-pause" @click="doPause" :disabled="submitting">
            <font-awesome-icon icon="pause" class="me-1" />
            {{ submitting ? 'Pausing…' : 'Pause Journey' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
export default {
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: { permission() { return this.store.state.getCurrentUserPermission; } },
  data() {
    return {
      journeys: [], meta: null, loading: false, submitting: false,
      search: '', filterStatus: '', page: 1,
      modalOpen: false, editing: null, deleteTarget: null, activateTarget: null, pauseTarget: null,
      form: { name:'', trigger_type:'manual', enrollment_type:'once', description:'', steps:[] },
      debounceTimer: null,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
    };
  },
  mounted() { this.fetchJourneys(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchJourneys() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/journeys', { params: { search: this.search, status: this.filterStatus, page: this.page, from: this.dateFrom || undefined, to: this.dateTo || undefined } });
        this.journeys = data.data?.data ?? data.data ?? data ?? [];
        this.meta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load journeys'); }
      finally { this.loading = false; }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchJourneys(); }, 400); },
    changePage(p) { this.page = p; this.fetchJourneys(); },
    applyDateFilter() { this.page = 1; this.fetchJourneys(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchJourneys(); },
    async doExport(format) {
      this.exportMenuOpen = false;
      this.exporting = true;
      try {
        const params = { format };
        if (this.search) params.search = this.search;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.dateFrom) params.from = this.dateFrom;
        if (this.dateTo) params.to = this.dateTo;
        const { data } = await axios.get('/crm/journeys/export', { params, responseType: 'blob' });
        const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
        const ext  = format === 'pdf' ? 'pdf' : 'csv';
        const ts   = new Date().toISOString().slice(0, 10);
        const url  = URL.createObjectURL(new Blob([data], { type: mime }));
        const a    = document.createElement('a');
        a.href = url; a.download = `journeys_${ts}.${ext}`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    openCreate() {
      this.editing = null;
      this.form = { name:'', trigger_type:'manual', enrollment_type:'once', description:'', steps:[] };
      this.modalOpen = true;
    },
    async openEdit(j) {
      this.editing = j.id;
      this.form = { name: j.name, trigger_type: j.trigger_type??'manual', enrollment_type: j.enrollment_type??'once', description: j.description??'', steps:[] };
      this.modalOpen = true;
      try {
        const { data } = await axios.get(`/crm/journeys/${j.id}`);
        const journey = data.data ?? data;
        this.form.steps = (journey.steps ?? []).map(s => ({
          type: s.type,
          config: s.config ?? this.defaultConfig(s.type),
        }));
      } catch { this.toast.error('Failed to load journey steps'); }
    },
    closeModal() { this.modalOpen = false; },
    confirmDelete(j) { this.deleteTarget = j; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) {
          await axios.put(`/crm/journeys/${this.editing}`, this.form);
          await axios.post(`/crm/journeys/${this.editing}/steps`, { steps: this.form.steps });
          this.toast.success('Journey updated');
        } else {
          await axios.post('/crm/journeys', this.form);
          this.toast.success('Journey created');
        }
        this.closeModal(); this.fetchJourneys();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    defaultConfig(type) {
      const map = {
        wait:         { delay_days: 1, delay_hours: 0 },
        email:        { subject: '', body: '' },
        sms:          { message: '' },
        update_field: { field: '', value: '' },
        create_task:  { title: '', due_in_days: 1 },
        condition:    { field: '', operator: 'equals', value: '' },
        end:          {},
      };
      return { ...(map[type] ?? {}) };
    },
    addStep() {
      this.form.steps.push({ type: 'wait', config: this.defaultConfig('wait') });
    },
    removeStep(i) { this.form.steps.splice(i, 1); },
    moveStep(i, dir) {
      const steps = this.form.steps;
      const j = i + dir;
      if (j < 0 || j >= steps.length) return;
      [steps[i], steps[j]] = [steps[j], steps[i]];
    },
    onStepTypeChange(i) {
      this.form.steps[i].config = this.defaultConfig(this.form.steps[i].type);
    },
    async doDelete() {
      this.submitting = true;
      try { await axios.delete(`/crm/journeys/${this.deleteTarget.id}`); this.toast.success('Deleted'); this.deleteTarget = null; this.fetchJourneys(); }
      catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },
    activateJourney(j) { this.activateTarget = j; },
    async doActivate() {
      this.submitting = true;
      try {
        await axios.post(`/crm/journeys/${this.activateTarget.id}/activate`);
        this.toast.success('Journey activated');
        this.activateTarget = null;
        this.fetchJourneys();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to activate'); }
      finally { this.submitting = false; }
    },
    pauseJourney(j) { this.pauseTarget = j; },
    async doPause() {
      this.submitting = true;
      try {
        await axios.post(`/crm/journeys/${this.pauseTarget.id}/pause`);
        this.toast.success('Journey paused');
        this.pauseTarget = null;
        this.fetchJourneys();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to pause'); }
      finally { this.submitting = false; }
    },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:180px;outline:none}
.filter-input:focus{border-color:#004AAD}
.filter-select{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--activate{color:#166534}
.btn-link--pause{color:#92400e}
.btn-link--del{color:#e03535}
.status-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.status--draft{background:#f1f5ff;color:#004AAD}
.status--active{background:#dcfce7;color:#166534}
.status--paused{background:#fef3c7;color:#92400e}
.status--completed{background:#e0f2fe;color:#075985}
.pagination{display:flex;align-items:center;justify-content:center;gap:12px;padding:14px;border-top:1px solid #f0f4fa}
.pg-btn{padding:6px 14px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-info{font-size:13px;color:#7a90b0}
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:520px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
.modal-box--journey{max-width:680px;overflow:visible}
.modal-body--journey{overflow-y:auto;max-height:calc(90vh - 130px)}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.modal-header-icon{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.modal-header-icon--green{background:#dcfce7;color:#166534}
.modal-header-icon--amber{background:#fef3c7;color:#92400e}
.modal-sub{font-size:12px;color:#7a90b0;margin:2px 0 0}
.action-card{background:#f4f7fb;border-radius:10px;padding:14px 16px;margin-bottom:14px}
.action-name{font-size:14px;font-weight:700;color:#1a2b45;margin:0 0 4px}
.action-meta{font-size:13px;color:#7a90b0;margin:0 0 2px}
.action-note{font-size:13px;color:#5a6a85;margin:0}
.btn-activate{background:#166534;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-activate:hover{background:#14532d}
.btn-activate:disabled{opacity:.55;cursor:not-allowed}
.btn-pause{background:#92400e;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-pause:hover{background:#7c3408}
.btn-pause:disabled{opacity:.55;cursor:not-allowed}
.form-row-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.steps-section{margin-top:18px;padding-top:16px;border-top:1.5px solid #e8edf5}
.steps-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.steps-title{font-size:13px;font-weight:700;color:#1a2b45;text-transform:uppercase;letter-spacing:.04em}
.btn-add-step{background:#f1f5ff;color:#004AAD;border:1.5px solid #c7d9f5;padding:6px 14px;border-radius:7px;font-size:12.5px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px}
.btn-add-step:hover{background:#e0eaff}
.steps-empty{font-size:13px;color:#b0beca;padding:10px 0}
.step-card{background:#f8fafd;border:1.5px solid #e4eaf2;border-radius:10px;padding:14px;margin-bottom:10px}
.step-card-head{display:flex;align-items:center;gap:8px;margin-bottom:10px}
.step-num{font-size:11px;font-weight:700;color:#7a90b0;min-width:46px}
.step-type-select{flex:1;padding:7px 10px;border:1.5px solid #dde5f0;border-radius:7px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
.step-btns{display:flex;gap:4px;margin-left:auto}
.step-btn{width:26px;height:26px;border:1.5px solid #dde5f0;border-radius:6px;background:#fff;color:#5a6a85;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0}
.step-btn:disabled{opacity:.35;cursor:not-allowed}
.step-btn--del{border-color:#fca5a5;color:#e03535}
.step-btn--del:hover{background:#fee2e2}
.step-config{display:flex;flex-direction:column;gap:10px}
.step-row{display:flex;gap:12px}
.step-row .form-group{flex:1;margin-bottom:0}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#fff;color:#1a2b45;border:1.5px solid #dde5f0;padding:8px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-export:hover{border-color:#004AAD;color:#004AAD}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;right:0;top:calc(100% + 4px);background:#fff;border:1.5px solid #e4eaf2;border-radius:9px;box-shadow:0 8px 28px rgba(0,0,0,.1);z-index:200;min-width:140px;padding:5px 0}
.export-opt{display:block;width:100%;background:none;border:none;padding:9px 16px;text-align:left;font-size:13px;color:#1a2b45;cursor:pointer}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
</style>
